import React from "react";
import { cn } from "@/lib/utils";

type SkeletonVariant =
  | "dashboard"
  | "cards"
  | "card-detail"
  | "table"
  | "list"
  | "form"
  | "grid"
  | "terminal"
  | "profile"
  | "stats";

interface PageSkeletonProps {
  variant?: SkeletonVariant;
  className?: string;
}

function S({ className }: { className?: string }) {
  return (
    <div className={cn("rounded-xl bg-muted relative overflow-hidden", className)}>
      <div className="absolute inset-0 skeleton-shimmer" />
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-5">
      <S className="h-8 w-48" />
      <S className="h-4 w-72" />
      <S className="h-36 rounded-2xl" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <S key={i} className="h-24" />)}
      </div>
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => <S key={i} className="h-16" />)}
      </div>
    </div>
  );
}

function CardsSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <S className="h-8 w-40" />
        <S className="h-10 w-32 rounded-lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-3">
            <S className="h-48 rounded-2xl" />
            <S className="h-5 w-3/4" />
            <S className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CardDetailSkeleton() {
  return (
    <div className="space-y-6">
      <S className="h-6 w-24" />
      <div className="flex flex-col md:flex-row gap-6">
        <S className="h-56 w-full md:w-96 rounded-2xl" />
        <div className="flex-1 space-y-4">
          <S className="h-7 w-48" />
          <S className="h-5 w-32" />
          <div className="grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => <S key={i} className="h-16" />)}
          </div>
        </div>
      </div>
      <S className="h-10 w-full" />
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => <S key={i} className="h-14" />)}
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <S className="h-7 w-40 mb-2" />
          <S className="h-4 w-64" />
        </div>
        <S className="h-10 w-40 rounded-lg" />
      </div>
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="bg-muted/50 px-4 py-3 flex gap-6">
          {[...Array(5)].map((_, i) => <S key={i} className="h-4 flex-1" />)}
        </div>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="px-4 py-3 flex gap-6 border-t border-border">
            {[...Array(5)].map((_, j) => <S key={j} className="h-4 flex-1" />)}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-4">
      <S className="h-7 w-40" />
      <S className="h-10 w-full max-w-sm rounded-lg" />
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => <S key={i} className="h-18 rounded-xl" />)}
      </div>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-5">
      <S className="h-7 w-48" />
      <S className="h-4 w-72" />
      <div className="space-y-4 max-w-lg">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-2">
            <S className="h-4 w-24" />
            <S className="h-10 rounded-lg" />
          </div>
        ))}
        <S className="h-11 w-full rounded-lg" />
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <S className="h-7 w-40" />
        <S className="h-10 w-32 rounded-lg" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="rounded-xl border border-border p-4 space-y-3">
            <S className="h-32" />
            <S className="h-5 w-3/4" />
            <S className="h-4 w-1/2" />
            <S className="h-9 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TerminalSkeleton() {
  return (
    <div className="space-y-3">
      <S className="h-12 rounded-lg" />
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3 space-y-2">
          {[...Array(12)].map((_, i) => <S key={i} className="h-6" />)}
        </div>
        <S className="col-span-6 h-[400px] rounded-xl" />
        <div className="col-span-3 space-y-3">
          <S className="h-10 rounded-lg" />
          <S className="h-10 rounded-lg" />
          <S className="h-32 rounded-lg" />
          <S className="h-11 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <S className="h-16 w-16 rounded-full" />
        <div className="space-y-2 flex-1">
          <S className="h-6 w-40" />
          <S className="h-4 w-56" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-xl border border-border p-4 space-y-3">
            <S className="h-5 w-32" />
            <S className="h-4 w-full" />
            <S className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="space-y-5">
      <div>
        <S className="h-7 w-48 mb-2" />
        <S className="h-4 w-72" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="rounded-xl border border-border p-4 space-y-2">
            <S className="h-5 w-5 mx-auto rounded" />
            <S className="h-6 w-16 mx-auto" />
            <S className="h-3 w-20 mx-auto" />
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => <S key={i} className="h-16" />)}
      </div>
    </div>
  );
}

const variants: Record<SkeletonVariant, React.FC> = {
  dashboard: DashboardSkeleton,
  cards: CardsSkeleton,
  "card-detail": CardDetailSkeleton,
  table: TableSkeleton,
  list: ListSkeleton,
  form: FormSkeleton,
  grid: GridSkeleton,
  terminal: TerminalSkeleton,
  profile: ProfileSkeleton,
  stats: StatsSkeleton,
};

export function InlineSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  const widths = ["w-full", "w-3/4", "w-5/6", "w-2/3", "w-4/5", "w-1/2"];
  return (
    <div className={cn("space-y-3 py-4", className)}>
      {[...Array(rows)].map((_, i) => (
        <S key={i} className={cn("h-4", widths[i % widths.length])} />
      ))}
    </div>
  );
}

export default function PageSkeleton({ variant = "dashboard", className }: PageSkeletonProps) {
  const Component = variants[variant];
  return (
    <div className={cn("animate-fade-in", className)}>
      <Component />
    </div>
  );
}
